import { useEffect, useState } from "react";
import {
  collection,
  collectionGroup,
  getCountFromServer,
  query,
  where,
} from "firebase/firestore";
import { db } from "@/config/firebase";

export interface NavBadges {
  claims: number;
  verifications: number;
  barSuggestions: number;
  bugReports: number;
  feedback: number;
  featureRequests: number;
  contentReports: number;
}

const EMPTY: NavBadges = {
  claims: 0,
  verifications: 0,
  barSuggestions: 0,
  bugReports: 0,
  feedback: 0,
  featureRequests: 0,
  contentReports: 0,
};

/**
 * Lädt Pending-Zähler periodisch nach, statt dauerhaft 7 Live-Listener
 * im gesamten Admin-Layout offen zu halten.
 */
export function useNavBadges(): NavBadges {
  const [badges, setBadges] = useState<NavBadges>(EMPTY);

  useEffect(() => {
    let cancelled = false;

    const refreshBadges = async () => {
      try {
        const [claims, verifications, barSuggestions, bugReports, feedback, featureRequests, contentReports] =
          await Promise.all([
            getCountFromServer(query(collection(db, "claimRequests"), where("status", "==", "pending"))),
            getCountFromServer(
              query(
                collectionGroup(db, "drinks"),
                where("isUserContributed", "==", true),
                where("approved", "==", false),
              )
            ),
            getCountFromServer(query(collection(db, "barSuggestions"), where("status", "==", "pending"))),
            getCountFromServer(query(collection(db, "bugReports"), where("status", "==", "pending"))),
            getCountFromServer(query(collection(db, "feedback"), where("status", "==", "new"))),
            getCountFromServer(query(collection(db, "featureRequests"), where("status", "==", "pending"))),
            getCountFromServer(query(collection(db, "contentReports"), where("status", "==", "pending"))),
          ]);

        if (cancelled) {
          return;
        }

        setBadges({
          claims: claims.data().count,
          verifications: verifications.data().count,
          barSuggestions: barSuggestions.data().count,
          bugReports: bugReports.data().count,
          feedback: feedback.data().count,
          featureRequests: featureRequests.data().count,
          contentReports: contentReports.data().count,
        });
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to refresh nav badges", error);
        }
      }
    };

    const refreshIfVisible = () => {
      if (document.visibilityState === "visible") {
        void refreshBadges();
      }
    };

    void refreshBadges();

    const intervalId = window.setInterval(refreshIfVisible, 60000);
    window.addEventListener("focus", refreshIfVisible);
    document.addEventListener("visibilitychange", refreshIfVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener("focus", refreshIfVisible);
      document.removeEventListener("visibilitychange", refreshIfVisible);
    };
  }, []);

  return badges;
}
